import { View, Text, StyleSheet, Svg, Polygon } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";

const COLOR_POSITIVE = "#3f8600";
const COLOR_NEGATIVE = "#cf1322";

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
    flexDirection: "column",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  label: {
    fontWeight: 600,
    color: "#1a1a1a",
  },
  latestDate: {
    color: "#888",
    marginBottom: 1,
  },
  mainValue: {
    fontWeight: 700,
    color: "#1a1a1a",
    lineHeight: 1,
  },
  deltaRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 1,
  },
  asymContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  asymSide: {
    flex: 1,
    flexDirection: "column",
  },
  asymDivider: {
    width: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    marginHorizontal: 4,
    alignSelf: "stretch",
  },
  benchmarks: {
    position: "absolute",
    bottom: 4,
    right: 6,
    flexDirection: "column",
    alignItems: "flex-end",
  },
});

function deltaColor(diff: number | null, lowGood: boolean | null): string {
  if (diff == null) return "#888";
  // null means neutral: a count of statuses is neither good nor bad, so the change
  // renders without a verdict rather than being coloured by the default direction.
  if (lowGood == null) return "#888";
  return (lowGood ? diff < 0 : diff >= 0) ? COLOR_POSITIVE : COLOR_NEGATIVE;
}

/** SVG triangle arrow + numeric value — guaranteed to render regardless of font glyph support. */
function DeltaRow({ diff, lowGood, fontSize, numDecimals }: { diff: number; lowGood: boolean | null; fontSize: number; numDecimals?: number | null }) {
  const color = deltaColor(diff, lowGood);
  const arrowSize = Math.max(4, fontSize * 0.85);
  // The backend reports the metric's decimal count. Without it keep the historical 2.
  const value = numDecimals != null ? Math.abs(diff).toFixed(numDecimals) : Math.round(Math.abs(diff) * 100) / 100;
  const points = diff >= 0 ? "5,0 10,10 0,10" : "0,0 10,0 5,10";

  return (
    <View style={s.deltaRow}>
      <Svg width={arrowSize} height={arrowSize} viewBox="0 0 10 10" style={{ marginRight: 1 }}>
        <Polygon points={points} fill={color} />
      </Svg>
      <Text style={{ fontSize, color }}>{value}</Text>
    </View>
  );
}

export function renderStat(
  snapshot: Extract<PdfWidgetSnapshot, { type: "stat_tile" }>,
  rect: PdfRect,
  scale: number,
) {
  const pad = Math.max(4, 12 * scale);
  const hasTeamPos = snapshot.showTeamPosData && (snapshot.team != null || snapshot.pos != null);

  // Font sizes scaled from browser px values to PDF points (scale = A4_WIDTH_PT / containerWidthPx).
  // Label matches new 18px Text strong title; others match CompactStatisticNumberSuffix px values.
  const labelSize = 10;
  const dateSize  = Math.max(5, 10 * scale);
  const mainSize  = Math.min(rect.height * 0.28, 35 * scale);
  const asymSize  = Math.min(rect.height * 0.28, 30 * scale);
  const deltaSize = Math.max(6, 13 * scale);
  const benchSize = snapshot.isAsym ? Math.max(5, 11 * scale) : Math.max(6, 15 * scale);

  // For asym with benchmarks, reserve bottom space to avoid overlap
  const bottomPad = hasTeamPos && snapshot.isAsym ? Math.max(8, benchSize * 2.8) : pad;
  // For asym with benchmarks, reserve right space to prevent content overlap
  const asymContainerStyle = hasTeamPos && snapshot.isAsym
    ? { ...s.asymContainer, marginRight: Math.max(10, benchSize * 4) }
    : s.asymContainer;

  return (
    <View style={[s.cell, { left: rect.left, top: rect.top, width: rect.width, height: rect.height, padding: pad, paddingBottom: bottomPad }]}>

      {/* Header: label — single line, overflow clipped to match rendered ellipsis behaviour */}
      {snapshot.label && (() => {
        const availableWidth = rect.width - 2 * pad;
        const maxChars = Math.floor(availableWidth / (labelSize * 0.5));
        const truncated = snapshot.label.length > maxChars ? snapshot.label.slice(0, maxChars) + "..." : snapshot.label;
        return (
          <Text style={[s.label, { fontSize: labelSize, marginBottom: 2 }]}>
            {truncated}
          </Text>
        );
      })()}

      {/* Latest date */}
      {snapshot.latestDate != null && (
        <Text style={[s.latestDate, { fontSize: dateSize }]}>{snapshot.latestDate}</Text>
      )}

      {/* Value + delta */}
      {snapshot.isAsym ? (
        <View style={asymContainerStyle}>
          <View style={s.asymSide}>
            <Text style={[s.mainValue, { fontSize: asymSize }]}>{snapshot.left?.value ?? "—"}</Text>
            {snapshot.left?.diff != null && (
              <DeltaRow diff={snapshot.left.diff} lowGood={snapshot.lowGood} fontSize={deltaSize} numDecimals={snapshot.numDecimals} />
            )}
          </View>
          <View style={s.asymDivider} />
          <View style={s.asymSide}>
            <Text style={[s.mainValue, { fontSize: asymSize }]}>{snapshot.right?.value ?? "—"}</Text>
            {snapshot.right?.diff != null && (
              <DeltaRow diff={snapshot.right.diff} lowGood={snapshot.lowGood} fontSize={deltaSize} numDecimals={snapshot.numDecimals} />
            )}
          </View>
        </View>
      ) : (
        <>
          {snapshot.latestValue != null && (
            <Text style={[s.mainValue, { fontSize: mainSize }]}>{snapshot.latestValue}</Text>
          )}
          {snapshot.diff != null && (
            <DeltaRow diff={snapshot.diff} lowGood={snapshot.lowGood} fontSize={deltaSize} numDecimals={snapshot.numDecimals} />
          )}
        </>
      )}

      {/* T/P benchmarks — absolute bottom-right, matching tile layout */}
      {hasTeamPos && (
        <View style={s.benchmarks}>
          {snapshot.team != null && (
            <Text style={{ fontSize: benchSize, color: snapshot.team.color, lineHeight: 1.4 }}>
              T: {snapshot.team.value}
            </Text>
          )}
          {snapshot.pos != null && (
            <Text style={{ fontSize: benchSize, color: snapshot.pos.color, lineHeight: 1.4 }}>
              P: {snapshot.pos.value}
            </Text>
          )}
        </View>
      )}
    </View>
  );
}
